<script setup lang="ts">
import { ref } from 'vue'
import StudentSettingsLayout from '@/Layouts/StudentSettingsLayout.vue'
import { Mail, Bell, Check, X, Smartphone } from 'lucide-vue-next'

interface NotificationPreferences {
  email_marketing: boolean
  email_lesson_reminders: boolean
  email_messages: boolean
  email_subscription_updates: boolean
  email_schedule_changes: boolean
  sms_lesson_reminders: boolean
  push_lesson_reminders: boolean
  push_messages: boolean
}

const props = defineProps<{
  preferences: NotificationPreferences
}>()

const form = ref<NotificationPreferences>({ ...props.preferences })
const isSaving = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const emailNotifications = [
  {
    key: 'email_marketing' as keyof NotificationPreferences,
    label: 'Marketing & Promotions',
    description: 'Receive updates about new features, special offers, and tips for learning',
  },
  {
    key: 'email_lesson_reminders' as keyof NotificationPreferences,
    label: 'Lesson Reminders',
    description: 'Get reminded about upcoming lessons and sessions',
  },
  {
    key: 'email_messages' as keyof NotificationPreferences,
    label: 'Tutor Messages',
    description: 'Receive email notifications when your tutor sends you a message',
  },
  {
    key: 'email_subscription_updates' as keyof NotificationPreferences,
    label: 'Subscription Updates',
    description: 'Get notified about billing, renewals, and subscription changes',
  },
  {
    key: 'email_schedule_changes' as keyof NotificationPreferences,
    label: 'Schedule Changes',
    description: 'Get notified when sessions are rescheduled or cancelled',
  },
]

const smsNotifications = [
  {
    key: 'sms_lesson_reminders' as keyof NotificationPreferences,
    label: 'Lesson Reminders',
    description: 'Receive an SMS reminder before your lessons start (requires a phone number on your profile)',
  },
]

const pushNotifications = [
  {
    key: 'push_lesson_reminders' as keyof NotificationPreferences,
    label: 'Lesson Reminders',
    description: 'Push notifications before your lessons start',
  },
  {
    key: 'push_messages' as keyof NotificationPreferences,
    label: 'Tutor Messages',
    description: 'Instant notifications when your tutor messages you',
  },
]

async function savePreferences() {
  isSaving.value = true
  errorMessage.value = ''

  try {
    await window.axios.put('/student/settings/notifications', form.value)

    successMessage.value = 'Notification preferences saved successfully!'
    setTimeout(() => { successMessage.value = '' }, 3000)
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || 'An error occurred'
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <StudentSettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <Check class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <X class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Email Notifications -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail class="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Email Notifications</h2>
            <p class="text-gray-500 text-sm">Choose which emails you'd like to receive</p>
          </div>
        </div>

        <div class="space-y-4">
          <div
            v-for="notification in emailNotifications"
            :key="notification.key"
            class="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
          >
            <div class="flex-1">
              <h3 class="font-medium text-gray-900">{{ notification.label }}</h3>
              <p class="text-sm text-gray-500 mt-0.5">{{ notification.description }}</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                v-model="form[notification.key]"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </section>

      <!-- SMS Notifications -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Smartphone class="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">SMS Notifications</h2>
            <p class="text-gray-500 text-sm">Get text message reminders on your phone</p>
          </div>
        </div>

        <div class="space-y-4">
          <div
            v-for="notification in smsNotifications"
            :key="notification.key"
            class="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
          >
            <div class="flex-1">
              <h3 class="font-medium text-gray-900">{{ notification.label }}</h3>
              <p class="text-sm text-gray-500 mt-0.5">{{ notification.description }}</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                v-model="form[notification.key]"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </section>

      <!-- Push Notifications -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Bell class="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Push Notifications</h2>
            <p class="text-gray-500 text-sm">Get instant updates on your devices</p>
          </div>
        </div>

        <div class="space-y-4">
          <div
            v-for="notification in pushNotifications"
            :key="notification.key"
            class="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
          >
            <div class="flex-1">
              <h3 class="font-medium text-gray-900">{{ notification.label }}</h3>
              <p class="text-sm text-gray-500 mt-0.5">{{ notification.description }}</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                v-model="form[notification.key]"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
        </div>

        <p class="text-sm text-gray-500 mt-4">
          Push notifications require browser permission. You may need to enable notifications in your browser settings.
        </p>
      </section>

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="savePreferences"
          :disabled="isSaving"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <svg v-if="isSaving" class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isSaving ? 'Saving...' : 'Save Preferences' }}
        </button>
      </div>
    </div>
  </StudentSettingsLayout>
</template>
